import React from 'react';

function formatClaimCountdown(nextClaimAt) {
    if (!nextClaimAt) return '—';
    const ms = nextClaimAt * 1000 - Date.now();
    if (ms <= 0) return 'Available now';
    const totalHours = Math.ceil(ms / (1000 * 60 * 60));
    if (totalHours < 48) {
        return `${totalHours}h remaining`;
    }
    const days = Math.ceil(totalHours / 24);
    return `${days} day${days === 1 ? '' : 's'} remaining`;
}

function formatClaimDate(nextClaimAt) {
    if (!nextClaimAt) return '—';
    try {
        return new Date(nextClaimAt * 1000).toLocaleString();
    } catch (_error) {
        return '—';
    }
}

/**
 * Employee-facing payroll claim card.
 */
const EmployeePayrollCard = ({
    payrollStatus,
    tokenSymbol = 'USDC',
    loading = false,
    claiming = false,
    onClaim,
    onRefresh,
}) => {
    if (loading) {
        return (
            <div className="rounded-2xl border border-indigo-200/70 bg-indigo-50/40 px-5 py-4 dark:border-indigo-900/40 dark:bg-indigo-950/20">
                <p className="text-sm text-indigo-900/80 dark:text-indigo-200">
                    Loading payroll status…
                </p>
            </div>
        );
    }

    if (!payrollStatus) {
        return null;
    }

    if (!payrollStatus.enrolled) {
        return (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/70 px-5 py-4 dark:border-amber-900/40 dark:bg-amber-950/20">
                <p className="text-sm font-semibold text-amber-950 dark:text-amber-100">
                    Monthly payroll not enrolled
                </p>
                <p className="mt-1 text-xs text-amber-900/80 dark:text-amber-200/80">
                    Your company must enroll and fund payroll on-chain before you can collect
                    salary.
                </p>
            </div>
        );
    }

    if (!payrollStatus.active) {
        return (
            <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-5 py-4 dark:border-zinc-800 dark:bg-zinc-900/40">
                <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">
                    Payroll inactive
                </p>
                <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
                    This enrollment was deactivated. Contact your company owner.
                </p>
            </div>
        );
    }

    const claimReady = Boolean(payrollStatus.claimable);
    const waitingForPeriod =
        !claimReady && payrollStatus.nextClaimAt * 1000 > Date.now();
    const unfunded =
        !claimReady &&
        !waitingForPeriod &&
        payrollStatus.vaultFunded === false;

    return (
        <div className="rounded-2xl border border-indigo-200/80 bg-gradient-to-br from-indigo-50/90 to-white px-5 py-5 shadow-sm dark:border-indigo-900/40 dark:from-indigo-950/30 dark:to-zinc-950">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-indigo-700 dark:text-indigo-300">
                        Monthly salary
                    </p>
                    <h3 className="mt-1 text-lg font-semibold text-zinc-900 dark:text-white">
                        {payrollStatus.amountFormatted} {tokenSymbol}
                        <span className="ml-2 text-sm font-normal text-zinc-500">
                            / period
                        </span>
                    </h3>
                    <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">
                        {claimReady
                            ? 'A period is due — collect salary to your connected wallet.'
                            : waitingForPeriod
                              ? `Next claim opens in ${formatClaimCountdown(payrollStatus.nextClaimAt)} (${formatClaimDate(payrollStatus.nextClaimAt)}).`
                              : unfunded
                                ? 'Period is due, but the company vault needs more funds. Ask your company to Fund payroll.'
                                : 'Payroll enrollment is active.'}
                    </p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                    {onRefresh ? (
                        <button
                            type="button"
                            onClick={onRefresh}
                            disabled={claiming}
                            className="rounded-xl border border-indigo-200 bg-white px-3 py-2.5 text-xs font-semibold text-indigo-900 transition hover:bg-indigo-50 disabled:opacity-50 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-100 dark:hover:bg-indigo-900/40"
                        >
                            Refresh
                        </button>
                    ) : null}
                    <button
                        type="button"
                        onClick={onClaim}
                        disabled={!claimReady || claiming}
                        className="rounded-xl bg-indigo-700 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-indigo-600 dark:hover:bg-indigo-500"
                    >
                        {claiming
                            ? 'Collecting…'
                            : claimReady
                              ? 'Collect salary'
                              : waitingForPeriod
                                ? formatClaimCountdown(payrollStatus.nextClaimAt)
                                : 'Not claimable'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmployeePayrollCard;
